import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  Star,
  Check,
  ArrowRight,
  Clock,
  Target,
  Zap,
  Shield,
  Heart,
  Crown,
  Menu,
  X
} from 'lucide-react';

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const heroRef = useRef(null);
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const trailRef = useRef([]);
  const starsRef = useRef([]);
  const particlesRef = useRef([]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Antigravity-style smooth blob + particle effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      if (heroRef.current) {
        canvas.width = heroRef.current.offsetWidth;
        canvas.height = heroRef.current.offsetHeight;
        initParticles();
      }
    };

    // Ambient floating particles - Structured grid for a Mobius-style ribbon
    const initParticles = () => {
      const isMobile = window.innerWidth < 768;

      // Initialize Stars - Fewer on mobile
      const starsCount = isMobile ? 80 : 200;
      const stars = [];
      for (let i = 0; i < starsCount; i++) {
        stars.push({
          x: Math.random() * canvasRef.current.width,
          y: Math.random() * canvasRef.current.height,
          size: Math.random() * (isMobile ? 1.2 : 1.5),
          opacity: Math.random() * 0.8 + 0.2
        });
      }
      starsRef.current = stars;

      // Initialize Infinity Ribbon Particles - Significantly fewer on mobile
      const particles = [];
      const uSteps = isMobile ? 25 : 40;
      const vSteps = isMobile ? 8 : 12;

      for (let i = 0; i < uSteps; i++) {
        for (let j = 0; j < vSteps; j++) {
          particles.push({
            u: (i / uSteps) * Math.PI * 2,
            v: (j / vSteps - 0.5) * 2,
            baseSize: 1.2,
          });
        }
      }
      particlesRef.current = particles;
    };

    resize();
    const ro = new ResizeObserver(resize);
    if (heroRef.current) ro.observe(heroRef.current);



    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const time = Date.now() * 0.0004;
      const scale = Math.min(canvas.width, canvas.height) * 0.32;

      // --- Draw Space Elements (Nebula & Stars) ---
      // Background Glow (Nebula)
      const nebula = ctx.createRadialGradient(canvas.width * 0.5, canvas.height * 0.5, 0, canvas.width * 0.5, canvas.height * 0.5, canvas.width * 0.6);
      nebula.addColorStop(0, 'rgba(59, 130, 246, 0.15)');
      nebula.addColorStop(0.5, 'rgba(56, 189, 248, 0.12)');
      nebula.addColorStop(1, 'rgba(10, 10, 26, 0)');
      ctx.fillStyle = nebula;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Bright Central Star
      const sun = ctx.createRadialGradient(canvas.width * 0.5, canvas.height * 0.4, 0, canvas.width * 0.5, canvas.height * 0.4, 100);
      sun.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
      sun.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = sun;
      ctx.beginPath();
      ctx.arc(canvas.width * 0.5, canvas.height * 0.4, 100, 0, Math.PI * 2);
      ctx.fill();

      // Static Stars
      starsRef.current.forEach(s => {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${s.opacity})`;
        ctx.fill();
      });

      // Calculate drifting positions for two ends
      const osc = (Math.sin(time * 0.4) + 1) / 2;
      const drift = osc * (canvas.width * 0.18);

      const centers = [
        { x: canvas.width * 0.12 + drift, y: canvas.height * 0.5, phase: 0 },
        { x: canvas.width * 0.88 - drift, y: canvas.height * 0.5, phase: Math.PI }
      ];

      // --- Draw Mouse Trail (Tail) ---
      const m = mouseRef.current;
      if (m.x > 0 && m.x < canvas.width && m.y > 0 && m.y < canvas.height) {
        const last = trailRef.current[0];
        if (!last || Math.hypot(m.x - last.x, m.y - last.y) > 10) {
          trailRef.current.unshift({ x: m.x, y: m.y, age: 0 });
        }
      }

      trailRef.current.forEach((t) => {
        t.age += 0.5;
        const life = 1 - t.age / 80;
        if (life > 0) {
          const size = 2 * life;
          const alpha = 0.6 * life;
          ctx.beginPath();
          ctx.arc(t.x, t.y, Math.max(0.1, size), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`; // White dots for space
          ctx.fill();
        }
      });
      trailRef.current = trailRef.current.filter(t => t.age < 80);

      // --- Draw Two Structured 3D Infinity Ribbons ---
      ctx.globalCompositeOperation = 'lighter'; // Glow effect

      centers.forEach((center) => {
        particlesRef.current.forEach((p) => {
          const u = p.u + time * 0.3 + center.phase;
          const v = p.v * Math.PI;

          const x3d = Math.cos(u) * (1 + 0.4 * Math.cos(v));
          const y3d = (Math.sin(2 * u) / 1.5) * (1 + 0.4 * Math.cos(v));
          const z3d = 0.4 * Math.sin(v);

          const ry = time * 0.2;
          const rz = time * 0.1;

          let x1 = x3d * Math.cos(ry) - z3d * Math.sin(ry);
          let z1 = x3d * Math.sin(ry) + z3d * Math.cos(ry);
          let x2 = x1 * Math.cos(rz) - y3d * Math.sin(rz);
          let y2 = x1 * Math.sin(rz) + y3d * Math.cos(rz);

          const fov = 2.5;
          const perspective = fov / (fov + z1);

          const x = center.x + x2 * scale * perspective;
          const y = center.y + y2 * scale * perspective;

          const depthFactor = (z1 + 1) / 2;
          const size = p.baseSize * perspective * (1.1 - depthFactor * 0.4);
          const alpha = 0.5 * (1 - depthFactor * 0.5) * perspective;

          ctx.beginPath();
          ctx.arc(x, y, Math.max(0.1, size), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(200, 230, 255, ${alpha})`; // Light blue/white glow
          ctx.fill();
        });
      });

      ctx.globalCompositeOperation = 'source-over';
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      ro.disconnect();
    };
  }, []);

  const handleHeroMouseMove = useCallback((e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const handleHeroMouseLeave = useCallback(() => {
    mouseRef.current = { x: -9999, y: -9999 };
  }, []);




  const features = [
    {
      icon: <Target className="h-8 w-8 text-blue-600" />,
      title: "Grade-Specific Learning",
      description: "Tailored practice tests for Grades 3, 5, 7, and 9 with age-appropriate content"
    },
    {
      icon: <Award className="h-8 w-8 text-sky-600" />,
      title: "Track Progress",
      description: "Monitor your improvement with detailed performance analytics and progress reports"
    },
    {
      icon: <Clock className="h-8 w-8 text-green-600" />,
      title: "Flexible Timing",
      description: "Practice at your own pace with unlimited access to all exam materials"
    },
    {
      icon: <Shield className="h-8 w-8 text-red-600" />,
      title: "Secure Platform",
      description: "Safe and reliable learning environment with data protection"
    }
  ];

  const stats = [
    { number: "Growing Fast", label: "Student Community" },
    { number: "100+", label: "Practice Tests" },
    { number: "95%", label: "Success Rate" },
    { number: "24/7", label: "Support Available" }
  ];



  const pricingPlans = [
    {
      name: 'Starter',
      price: '$9.99',
      icon: Zap,
      color: 'blue',
      features: [
        'Access 3 exam sets',
        '10+ Practice Tests',
        '360+ Questions and answer explanations',
        'Available for grade 3, 5, 7, and 9',
        'Reading, Writing, Numeracy and Language',
        'Progress Tracking',
        'Email Support'
      ],
      highlighted: false,
      visible: true
    },
    {
      name: 'Standard',
      price: '$14.99',
      icon: Star,
      color: 'purple',
      features: [
        'Access 6 exam sets',
        '24 Practice Tests',
        '700+ Questions and answer explanations',
        'Available for grade 3, 5, 7, and 9',
        'Reading, Writing, Numeracy and Language',
        'Progress Tracking',
        'Email Support'
      ],
      highlighted: true,
      visible: true
    },
    {
      name: 'Premium',
      price: '$19.99',
      icon: Crown,
      color: 'gold',
      features: [
        'Access 10 exam sets',
        '40 Practice Tests',
        '1200+ Questions and answer explanations',
        'Available for grade 3, 5, 7, and 9',
        'Reading, Writing, Numeracy and Language',
        'Progress Tracking',
        'Email Support'
      ],
      highlighted: false,
      visible: false // Change to true to enable this package on the landing page
    }
  ];

  const visiblePlans = pricingPlans.filter(plan => plan.visible !== false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-100">
      <style>
        {`
          @keyframes orbit1 {
            from { transform: rotate(0deg) translateX(45px) rotate(0deg); }
            to { transform: rotate(360deg) translateX(45px) rotate(-360deg); }
          }
          @keyframes orbit2 {
            from { transform: rotate(0deg) translateX(55px) rotate(0deg); }
            to { transform: rotate(-360deg) translateX(55px) rotate(360deg); }
          }
          @keyframes orbit3 {
            from { transform: rotate(0deg) translateX(35px) rotate(0deg); }
            to { transform: rotate(180deg) translateX(35px) rotate(-180deg); }
          }
        `}
      </style>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-white shadow-lg py-2' : 'bg-transparent py-4'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3 relative">
              {/* Logo Glow Effect - Enhanced */}
              {!isScrolled && !isMobileMenuOpen && (
                <>
                  <div className="absolute -inset-8 bg-blue-500/30 blur-3xl rounded-full animate-pulse pointer-events-none" />
                  <div className="absolute -inset-4 bg-sky-500/25 blur-2xl rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />

                  {/* Orbiting Bodies */}
                  <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-blue-400 rounded-full blur-[1px] shadow-[0_0_8px_rgba(96,165,250,0.8)]" style={{ animation: 'orbit1 4s linear infinite' }} />
                  <div className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-sky-400 rounded-full blur-[1px] shadow-[0_0_8px_rgba(56,189,248,0.8)]" style={{ animation: 'orbit2 7s linear infinite' }} />
                  <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-teal-400 rounded-full blur-[1px] shadow-[0_0_8px_rgba(45,212,191,0.8)]" style={{ animation: 'orbit3 10s linear infinite', animationDelay: '-2s' }} />
                </>
              )}
              <img
                src="/logo/nep-logo.png"
                alt="NEP"
                className="h-12 w-auto relative z-10"
              />
              <div className="flex flex-col relative z-10">
                <span className={`text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>NEP</span>
                <span className={`text-xs font-medium tracking-wide transition-colors ${isScrolled ? 'text-gray-600' : 'text-gray-200'}`}>NextGen Exam Platform</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-3">
              <Link
                to="/contact"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${isScrolled
                  ? 'text-gray-700 hover:text-blue-600'
                  : 'text-white hover:text-blue-300'
                  }`}
              >
                Contact Us
              </Link>
              <Link
                to="/signin"
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${isScrolled
                  ? 'text-gray-700 hover:text-blue-600'
                  : 'text-white hover:text-blue-300'
                  }`}
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg"
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-300'
                } hover:bg-gray-100/10`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-3">
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                >
                  Contact Us
                </Link>
                <Link
                  to="/signin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="mx-4 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-all text-center"
                >
                  Get Started
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section
        ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        onMouseLeave={handleHeroMouseLeave}
        className="relative pt-24 md:pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0a0a1a] overflow-hidden"
      >
        {/* Mouse-following dot trail canvas — hero only */}
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <div className="max-w-7xl mx-auto text-center" style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-sky-500 via-sky-300 to-white bg-clip-text text-transparent">
              Excel in Your Exams with NEP Learning
            </span>
          </h1>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Practice tests designed for Australian students. Track your progress,
            improve your scores, and achieve academic excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/packages"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-xl"
              style={{ cursor: 'pointer' }}
            >
              Start Learning Today
              <ArrowRight className="ml-2 h-5 w-5 inline" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose NEP?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We provide everything your child needs to succeed in their academic journey
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex justify-center mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Parents & Students Say
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Join thousands of satisfied learners across Australia
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Pricing Section */}
      <section className="py-20 bg-fixed relative overflow-hidden">
        {/* Background Image - Vivid Moving Lights */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("./img/bg-new4.jpg")',
          }}
        />
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4 relative z-10">
              Choose Your Learning Plan
            </h2>
            <p className="text-xl text-white/90 relative z-10">
              Flexible pricing options for every learning need
            </p>
          </div>
          <div className={`grid grid-cols-1 gap-8 ${visiblePlans.length === 1
            ? 'max-w-md mx-auto'
            : visiblePlans.length === 2
              ? 'md:grid-cols-2 max-w-4xl mx-auto'
              : 'md:grid-cols-3'
            }`}>
            {visiblePlans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${plan.highlighted
                  ? 'border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-white'
                  : plan.color === 'blue'
                    ? 'border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white'
                    : 'border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-white'
                  }`}
              >
                {plan.highlighted && (
                  <>
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-1">
                        <Star className="h-4 w-4" />
                        <span>Most Popular</span>
                      </div>
                    </div>
                    {/* {plan.name === 'Standard' && (
                      <div className="absolute -top-6 right-2 z-20">
                        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-full text-sm font-bold animate-bounce shadow-lg transform rotate-12 hover:rotate-0 transition-transform duration-300">
                          <span className="flex items-center space-x-1">
                            <span className="text-lg">🔥</span>
                            <span>50% OFF</span>
                          </span>
                        </div>
                      </div>
                    )} */}
                  </>
                )}

                <div className="p-8">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${plan.highlighted
                        ? 'bg-purple-100'
                        : plan.color === 'blue'
                          ? 'bg-blue-100'
                          : 'bg-yellow-100'
                        }`}>
                        <plan.icon className={`h-6 w-6 ${plan.highlighted
                          ? 'text-purple-600'
                          : plan.color === 'blue'
                            ? 'text-blue-600'
                            : 'text-yellow-600'
                          }`} />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">
                        {plan.price}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-8">
                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3">
                          <Check className={`h-5 w-5 ${plan.highlighted ? 'text-purple-600' : plan.color === 'blue' ? 'text-blue-600' : 'text-yellow-600'} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <Link
                    to="/packages"
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${plan.highlighted
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800'
                      : plan.color === 'blue'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700'
                      }`}
                  >
                    <span>Get Started</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Start Learning?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of students achieving academic excellence
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/packages"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105"
            >
              Get Start
              <Zap className="ml-2 h-5 w-5 inline" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-all"
            >
              Contact Us
              <Heart className="ml-2 h-5 w-5 inline" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img
                  src="/logo/nep-logo.png"
                  alt="NEP"
                  className="h-8 w-auto"
                />
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-white">NEP</span>
                  <span className="text-xs text-gray-400">NextGen Exam Platform</span>
                </div>
              </div>
              <p className="text-gray-400">
                Empowering students to achieve academic excellence through innovative learning solutions.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                {/* <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li> */}
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/policies" className="hover:text-white transition-colors">Policies</Link></li>

              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Resources</h3>
              <ul className="space-y-2 text-gray-400">
                {/* <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li> */}
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                {/* <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/support" className="hover:text-white transition-colors">Support</Link></li> */}
              </ul>
            </div>
            <div>
              {/* <h3 className="text-lg font-semibold mb-4">Connect</h3> */}
              <div className="flex space-x-4">
                {/* <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Users className="h-6 w-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <BookOpen className="h-6 w-6" />
                </a> */}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2026 Nextgen Exam Platform. All rights reserved.</p>
            <p className="mt-2 text-sm">QBYTE Technology Pty Ltd | ABN: 78697357510 | ACN: 697 357 510</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
